// Versand-Report einer Brevo-Kampagne: Zustellung, Öffnungen, Klicks je Liste + Belegung des Festwochenendes (Buchungen seit Versand)
import React, { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, Loader2, RefreshCw, MailOpen, MousePointerClick, AlertTriangle, BedDouble, Send, XCircle, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => (d ? new Date(d).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "–");
const fmtBerlin = (d) => (d ? `${new Date(d).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" })} Uhr` : "–");

export function CampaignReport({ campaignId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    setBusy(true); setErr("");
    try { const r = await axios.get(`${API}/api/brevo/campaigns/${campaignId}/report`); setData(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "Report konnte nicht geladen werden"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="text-xs text-rose-400 mt-2" data-testid="campaign-report-error">{err}</div>;
  if (!data) return <div className="text-xs text-eb-muted mt-2 flex items-center gap-1" data-testid="campaign-report-loading"><Loader2 size={12} className="animate-spin" /> Report wird geladen …</div>;
  const t = data.totals; const av = data.availability;
  return (
    <div className="mt-3 space-y-3" data-testid="campaign-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline flex items-center gap-1"><BarChart3 size={12} /> Versand-Report · Stand {fmt(data.fetched_at)}</div>
        <button className="btn !py-1 text-xs" onClick={load} disabled={busy} data-testid="campaign-report-refresh">{busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Aktualisieren</button>
      </div>
      {data.brevo_error && (
        <div className="text-xs text-amber-400 flex items-start gap-1" data-testid="campaign-report-brevo-error"><AlertTriangle size={12} className="mt-0.5" />
          Brevo-Statistik aktuell nicht abrufbar: {data.brevo_error.includes("unrecognised IP") ? `Brevo blockiert die Server-IP ${(data.brevo_error.match(/IP address ([0-9.]+)/) || [])[1] || ""} – unter app.brevo.com → Sicherheit → Autorisierte IPs freigeben oder die IP-Beschränkung deaktivieren.` : data.brevo_error}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="campaign-report-totals">
        <Kpi label="Zugestellt" value={`${t.delivered} / ${t.sent}`} />
        <Kpi label="Öffnungen" value={t.opens} sub={`${t.open_rate} %`} icon={<MailOpen size={12} />} />
        <Kpi label="Klicks auf Buchen" value={t.clicks} sub={`${t.click_rate} %`} icon={<MousePointerClick size={12} />} />
        <Kpi label="Bounces" value={t.soft_bounces + t.hard_bounces} sub={`${t.hard_bounces} hart`} icon={<AlertTriangle size={12} />} />
        <Kpi label="Abmeldungen" value={t.unsubscribes} sub={t.complaints ? `${t.complaints} Beschwerden` : ""} />
      </div>
      <table className="w-full text-xs" data-testid="campaign-report-runs"><thead><tr className="text-eb-muted text-left">
        <th className="py-1">Liste</th><th>Versand</th><th>Zugestellt</th><th>Geöffnet</th><th>Geklickt</th><th>Bounces</th><th>Abgemeldet</th></tr></thead>
        <tbody>{data.runs.map((r) => (
          <tr key={r.brevo_campaign_id} className="border-t border-white/5" data-testid={`campaign-report-run-${r.brevo_campaign_id}`}>
            <td className="py-1 font-medium">{r.source_name} <span className="text-eb-muted font-mono">#{r.brevo_campaign_id}</span></td>
            <td>{fmt(r.sent_at)}</td><td>{r.delivered} / {r.sent}</td><td>{r.opens}</td><td>{r.clicks}</td><td>{r.soft_bounces + r.hard_bounces}</td><td>{r.unsubscribes}</td>
          </tr>))}</tbody></table>

      {av && !av.error && (
        <div className="eb-surface rounded-md p-3" data-testid="campaign-report-bookings">
          <div className="overline flex items-center gap-1 mb-1"><BedDouble size={12} /> Buchungen Festwochenende · {av.totals.booked_since} Bungalow(s) seit {fmt(av.baseline_at)} belegt · noch {av.totals.free_2n} von {av.totals.units} frei (2 Nächte)</div>
          <table className="w-full text-xs"><thead><tr className="text-eb-muted text-left"><th className="py-1">Kategorie</th><th>Einheiten</th><th>Frei Fr–So</th>{av.has_1n && <th>Frei Sa–So</th>}<th>Belegt seit Versand</th></tr></thead>
            <tbody>{av.rows.map((r) => (
              <tr key={r.category_id} className="border-t border-white/5" data-testid={`campaign-report-cat-${r.category_id}`}>
                <td className="py-1">{r.name} <span className="text-eb-muted">· {r.tier}</span></td><td>{r.units}</td><td>{r.free_2n}</td>{av.has_1n && <td>{r.free_1n}</td>}
                <td className={r.booked_since ? "text-emerald-400 font-semibold" : ""}>{r.booked_since}</td>
              </tr>))}</tbody></table>
          <div className="text-[10px] text-eb-muted mt-1">Belegung laut easybooking-Verfügbarkeit (alle Kanäle), nicht nur Newsletter-Buchungen.</div>
        </div>
      )}
      {av?.error && <div className="text-xs text-amber-400">Belegung nicht abrufbar: {av.error}</div>}

      <FollowupBlock campaignId={campaignId} />
      <BounceBlock campaignId={campaignId} />
    </div>
  );
}

function FollowupBlock({ campaignId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [days, setDays] = useState(3);
  const base = `${API}/api/brevo/campaigns/${campaignId}/followup`;
  const loadState = async () => { const r = await axios.get(base); setState(r.data && Object.keys(r.data).length ? r.data : null); };
  useEffect(() => { loadState(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (dry, sendNow) => {
    setBusy(dry ? "plan" : "go"); setMsg("");
    try {
      const r = await axios.post(base, { dry_run: dry, send_now: !!sendNow, days: Number(days) });
      setState(r.data);
      setMsg(dry ? `${r.data.counts.recipients} Empfänger ermittelt (Öffner ohne Klick)`
        : sendNow ? `Nachfass-Mail an ${r.data.added} Empfänger verschickt` : `Nachfass-Mail für ${fmtBerlin(r.data.scheduled_at)} eingeplant`);
    } catch (e) { setMsg(e.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const cancel = async () => {
    setBusy("cancel");
    try { await axios.post(`${base}/cancel`); setMsg("Geplante Nachfass-Mail abgebrochen"); await loadState(); }
    catch (e) { setMsg(e.response?.data?.detail || "Abbruch fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const c = state?.counts;
  return (
    <div className="eb-surface rounded-md p-3 space-y-2" data-testid="followup-block">
      <div className="overline flex items-center gap-1"><Send size={12} /> Nachfass-Mail · nur an Öffner ohne Klick</div>
      {c && (
        <div className="text-xs" data-testid="followup-counts">
          {c.openers} Öffner · {c.clickers} davon haben geklickt · <span className="text-emerald-400 font-semibold">{c.recipients} Empfänger</span> für die Erinnerung
          {c.unsubscribed ? ` · ${c.unsubscribed} Abmeldungen ausgeschlossen` : ""}{c.soft_bounces ? ` · ${c.soft_bounces} Bounces ausgeschlossen` : ""}
        </div>
      )}
      {state?.subject && <div className="text-xs text-eb-muted" data-testid="followup-subject">Betreff: „{state.subject}“</div>}
      {state?.status === "scheduled" && (
        <div className="text-xs text-amber-300" data-testid="followup-scheduled">
          Geplant für {fmtBerlin(state.scheduled_at)} · Brevo-Kampagne #{state.brevo_campaign_id} · {state.added} Empfänger
        </div>
      )}
      {state?.status === "sent" && <div className="text-xs text-emerald-400" data-testid="followup-sent">Verschickt an {state.added} Empfänger</div>}
      {state?.status === "cancelled" && <div className="text-xs text-eb-muted" data-testid="followup-cancelled">Abgebrochen</div>}
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn !py-1 text-xs" onClick={() => run(true)} disabled={!!busy} data-testid="followup-plan">
          {busy === "plan" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Empfänger ermitteln
        </button>
        <label className="text-xs text-eb-muted flex items-center gap-1">in
          <input type="number" min="0" max="30" value={days} onChange={(e) => setDays(e.target.value)} className="w-14" data-testid="followup-days" /> Tagen, 10 Uhr
        </label>
        <button className="btn btn-primary !py-1 text-xs" onClick={() => run(false, false)} disabled={!!busy || !c?.recipients || state?.status === "scheduled"} data-testid="followup-schedule">
          {busy === "go" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Nachfass-Mail einplanen
        </button>
        {state?.status === "scheduled" && (
          <button className="btn !py-1 text-xs" onClick={cancel} disabled={!!busy} data-testid="followup-cancel">
            {busy === "cancel" ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Geplanten Versand abbrechen
          </button>
        )}
      </div>
      {msg && <div className="text-xs text-eb-muted" data-testid="followup-msg">{msg}</div>}
    </div>
  );
}

function BounceBlock({ campaignId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const base = `${API}/api/brevo/campaigns/${campaignId}/bounces`;
  useEffect(() => { (async () => { const r = await axios.get(base); setState(Object.keys(r.data || {}).length ? r.data : null); })(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (dry) => {
    setBusy(dry ? "check" : "clean"); setMsg("");
    try {
      const r = await axios.post(`${base}/clean`, { dry_run: dry, remove_soft_from_lists: true, delete_hard_contacts: true });
      setState(r.data);
      setMsg(dry ? `${r.data.permanent} dauerhaft unerreichbar, ${r.data.temporary} vorübergehend`
        : `${r.data.deleted_contacts} Kontakte gelöscht, ${r.data.removed_from_lists} Listeneinträge entfernt, ${r.data.local_deleted} lokal gelöscht`);
    } catch (e) { setMsg(e.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  return (
    <div className="eb-surface rounded-md p-3 space-y-2" data-testid="bounce-block">
      <div className="overline flex items-center gap-1"><Trash2 size={12} /> Bounce-Pflege · DSGVO</div>
      <div className="text-[11px] text-eb-muted">
        Dauerhaft unerreichbare Adressen (harte Bounces und „Adresse existiert nicht“) werden in Brevo vollständig gelöscht (Art. 17 DSGVO),
        vorübergehende Bounces (Postfach voll, Timeout) nur aus den Gästelisten entfernt – lokale Kontakte ebenfalls.
      </div>
      {state && (
        <div className="text-xs" data-testid="bounce-counts">
          {state.hard_bounces} harte · {state.soft_bounces} weiche Bounces · davon <span className="text-rose-400 font-semibold">{state.permanent} dauerhaft unerreichbar</span> (werden gelöscht) · {state.temporary} vorübergehend (nur aus Listen entfernt) · Listen: {(state.source_lists || []).join(", ") || "–"}
          {state.status === "done" && <span className="text-emerald-400 font-semibold"> · bereinigt: {state.deleted_contacts} gelöscht, {state.removed_from_lists} aus Listen entfernt, {state.local_deleted} lokal</span>}
        </div>
      )}
      {state?.permanent_sample?.length > 0 && (
        <div className="text-[10px] text-eb-muted" data-testid="bounce-sample">
          Beispiele: {state.permanent_sample.map((s) => `${s.email} (${(s.reason || "").slice(0, 40)})`).join(" · ")}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn !py-1 text-xs" onClick={() => run(true)} disabled={!!busy} data-testid="bounce-check">
          {busy === "check" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Bounces prüfen
        </button>
        <button className="btn !py-1 text-xs" onClick={() => run(false)} disabled={!!busy || !state} data-testid="bounce-clean">
          {busy === "clean" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Jetzt bereinigen
        </button>
      </div>
      {msg && <div className="text-xs text-eb-muted" data-testid="bounce-msg">{msg}</div>}
    </div>
  );
}

function Kpi({ label, value, sub, icon }) {
  return (
    <div className="eb-surface rounded-md p-2" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="text-[10px] text-eb-muted flex items-center gap-1">{icon}{label}</div>
      <div className="font-mono text-base">{value}</div>
      {sub ? <div className="text-[10px] text-eb-muted">{sub}</div> : null}
    </div>
  );
}
